/**
 *  @function AldwynCrossStoreCheckout
 *  @description
 *  When the user clicks the checkout button (button[name="checkout"]) on the CURRENT STORE,
 *  the script reads the cart, finds the matching variant on the TARGET store (aldwynco.com),
 *  then redirects to a cart permalink of the form https://aldwynco.com/cart/ID:QTY,ID:QTY.
 *
 *  Architecture: MATCH FIRST -> REDIRECT AFTER. If ANY line cannot be resolved,
 *  CANCEL the whole redirect and let the native checkout run as usual (no partial redirect).
 *
 *  Both stores share this SAME theme, so the script detects the host at runtime and does NOTHING
 *  when it is running on aldwynco.com itself (so the native checkout runs).
 *
 *  HOW TO EMBED (add at the end of layout/theme.liquid, right before </body>, indented with TAB like the other lines):
 *    <script src="{{ 'aldwyn-cross-store-checkout.js' | asset_url }}" defer="defer"></script>
 */

(function () {
  'use strict';

  // Guard against attaching the listener twice (if the file is embedded twice)
  if (window.__aldwynCrossStoreCheckout) return;
  window.__aldwynCrossStoreCheckout = true;

  /* =========================================================================
   * ====  CONFIGURATION — EDIT BY HAND HERE  ================================
   * =========================================================================
   * Ctrl+F the group name to jump straight to the place to edit, e.g. type "GROUP 3":
   *   GROUP 1  DAILY ORDER CAPS: total orders, orders per site, orders per product
   *   GROUP 2  RUN TIME: time windows, time on page
   *   GROUP 3  PRODUCTS: buy only 1 unit
   *   GROUP 4  VISITOR BLOCKING: country, IP, device
   *   GROUP 5  DOMAIN: target store
   *   GROUP 6  TEST SWITCHES: verbose logging, force device/country/IP
   *   GROUP 7  TECHNICAL: permalink, loading target store data, fallback mode
   * =======================================================================*/

  /* =========================================================================
   * ====  GROUP 1: DAILY ORDER CAPS  ========================================
   * =======================================================================*/

  /* ===== CONDITION: STOP BY ALDWYN'S ORDER COUNT FOR THE DAY ==============
   * If aldwynco's PAID order count SINCE 00:00 VIETNAM TIME today reaches one of the thresholds
   * below, cross-store STOPS running (clicking checkout -> native checkout). Runs ALONGSIDE the other
   * conditions — every condition must pass before the customer is sent to aldwyn.
   *
   * IMPORTANT: theme JS CANNOT read orders by itself (private data that needs an admin token — which must
   * never be embedded in a public file). The numbers must come from an ENDPOINT you deploy yourself (see
   * aldwyn-order-total-worker.js in the project root). Put the endpoint URL in ORDER_TOTAL_ENDPOINT.
   * ========================================================================*/
  const DAILY_LIMIT_ENABLED = true;

  // (a) ORDER THRESHOLD. PAID orders today > threshold -> cross-store does NOT run.
  // E.g. 20 -> redirects stop at the 21st order (the 20th order still goes through).
  // SET TO 0 -> turns OFF only this condition (the thresholds below still apply).
  // The number comes from the endpoint's "orders" field (the worker already returns it, no worker change needed).
  const MAX_DAILY_ORDERS = 60;

  // (b) SEPARATE ORDER THRESHOLD FOR EACH SOURCE SITE. INDEPENDENT of (a): hitting either one stops.
  // Key = hostname of the source store (lowercase, WITHOUT 'www.', without https://).
  // Value = MAXIMUM orders that site may send to aldwyn per day. Written in ONE of two forms:
  //   'leanors.com': 5  -> ONE LEVEL ALL DAY: once leanors has 5 orders, the 6th uses the NATIVE CHECKOUT.
  //   'solfootwear.com': { '01:30': 5, '03:30': 10 }  -> RISING BY TIME STEP: from 01:30 the cap is 5,
  //     from 03:30 the cap is 10. To change it just add/edit/remove a step line, no need to keep them sorted.
  // Rules for the time-step form:
  //   - A cap holds until the next step, so steps do NOT need to line up with ACTIVE_HOUR_RANGES.
  //   - The cap is compared with the TOTAL orders since 00:00 today, NOT per window: with 5 orders already,
  //     the cap-10 step only allows 5 more orders.
  //   - Before the first step of the day (e.g. window 00:45-00:59) -> the first step's cap is used.
  //   - Time is read in ACTIVE_HOURS_TIMEZONE, the same clock as ACTIVE_HOUR_RANGES.
  //   - Cap 0 means NO separate limit, not "stop redirecting".
  // Difference from (a): (a) counts the TOTAL across all sites. E.g. leanors hits its cap of 5 while halosneaker
  // has only 1 order: halosneaker KEEPS running — until the TOTAL reaches MAX_DAILY_ORDERS, then every site stops.
  // The number comes from the endpoint's "by_referrer_paid" field (the worker already returns it).
  const MAX_DAILY_ORDERS_PER_SITE = {
    'solfootwear.com': {
      '01:00': 5,
      '02:00': 10,
      '03:00': 15,
      '04:00': 20,
      '05:00': 25,
      '06:00': 30,
      '07:00': 30
    }
  };

  // Threshold for sites NOT listed in the table above. 0 = unknown sites have no separate limit
  // (the total threshold (a) still applies).
  const MAX_DAILY_ORDERS_PER_SITE_DEFAULT = 0;

  // (c) SEPARATE ORDER THRESHOLD FOR EACH PRODUCT. INDEPENDENT of (a) and (b): hitting any one stops.
  // Key = the product link's HANDLE (the part after /products/) on aldwynco OR on the current store;
  // the product title also works (both are normalized when matching), or paste the whole product LINK,
  // e.g. 'https://solfootwear.com/products/vintage-ankle-boots' (the script only takes the handle after /products/).
  // Value = MAXIMUM orders per day for that product, written in ONE of the two forms as in (b):
  //   'link-a': 15                           -> ONE LEVEL ALL DAY.
  //   'link-b': { '00:45': 4, '04:00': 16 }  -> RISING BY TIME STEP, same time-step rules as (b).
  // Rules:
  //   - Uses '>=' like (b): cap 15 -> stops at 15 orders, the 16th order uses the NATIVE CHECKOUT.
  //   - Counted per PRODUCT on aldwyn, across all source sites: two sites selling link-a share one cap.
  //   - An order containing both link-a and link-b counts for BOTH -> real total orders can be LOWER than the sum of caps.
  //   - Multi-product cart: if just ONE product has hit its cap -> the WHOLE CART uses the NATIVE CHECKOUT (no cart splitting).
  //   - Products not in the table, or cap 0 -> NO separate limit ((a) and (b) still apply).
  // The number comes from the endpoint's "by_product_paid" field: { '<product_id on aldwyn>': { orders: <order count> } }.
  // If the endpoint does not return this field yet -> the caps in the table have NO effect while PROCEED_IF_LIMIT_UNKNOWN = true.
  // LEAVE THE TABLE EMPTY {} -> turns this condition OFF.
  const MAX_DAILY_ORDERS_PER_PRODUCT = {
    
  };

  // Endpoint URL returning JSON like {"orders": <order count>, ...}. LEAVE EMPTY '' -> condition OFF.
  const ORDER_TOTAL_ENDPOINT = 'https://raspy-salad-48d3.ntd270799.workers.dev/';

  // Numbers unavailable (endpoint error/timeout)? true = STILL run (fail-open). false = native checkout.
  // Applies to ALL THREE order thresholds.
  const PROCEED_IF_LIMIT_UNKNOWN = true;

  // How long to cache the numbers (ms). Orders change all day long, so keep it SHORT. 60000 = 60 seconds.
  const ORDER_TOTAL_TTL_MS = 60 * 1000;

  // Maximum wait when calling the endpoint (ms).
  const ORDER_TOTAL_TIMEOUT_MS = 4000;

  /* =========================================================================
   * ====  GROUP 2: RUN TIME  ================================================
   * =======================================================================*/

  /* ===== TIME WINDOW GATE: run cross-store only within these time ranges ===
   * Redirect to aldwynco ONLY within the time windows below.
   * Outside the windows -> clicking checkout uses the NATIVE CHECKOUT as usual.
   * ========================================================================*/

  // Each range is written as [start, end): the start IS included, the end is NOT.
  //   [[9, 17]]              -> 9:00 to 16:59 (stops exactly at 17:00)
  //   [[8, 12], [13, 22]]    -> 8:00-11:59 AND 13:00-21:59 (off during 12:00-12:59)
  //   [[22, 6]]              -> ACROSS MIDNIGHT: 22:00-23:59 and 00:00-05:59
  //   [['09:30', '17:45']]   -> use 'HH:MM' strings when you need minutes
  //   [[0, 24]]              -> all day
  // LEAVE THE ARRAY EMPTY [] -> time window gate OFF (runs at any time).
  // NOTE: a range whose start EQUALS its end (e.g. [9, 9]) is an EMPTY range -> never matches.
  const ACTIVE_HOUR_RANGES = [
    ['01:30', '02:00'],
    ['02:30', '03:00'],
    ['03:30', '06:45'],
    ['08:30', '09:45'],
    ['12:15', '13:30'],
    ['18:15', '18:45'],
    ['19:15', '19:45'],
    ['21:00', '21:30'],
    ['23:00', '23:59']
  ];
  // const ACTIVE_HOUR_RANGES = [];

  // Time zone used to read the "current time" for ACTIVE_HOUR_RANGES.
  // LEAVE EMPTY '' -> uses the VISITOR'S DEVICE CLOCK: for a visitor in the US, 9:00 means 9:00 US time.
  // Enter an IANA time zone name to LOCK to one standard clock wherever the visitor is:
  //   'Asia/Ho_Chi_Minh' (Vietnam time), 'America/New_York', 'America/Los_Angeles', 'UTC'
  // Invalid time zone / unsupported browser -> falls back to the visitor's clock (never breaks checkout).
  const ACTIVE_HOURS_TIMEZONE = 'Asia/Ho_Chi_Minh';

  // TIME-ON-PAGE GATE: the visitor must have been ON THE CURRENT PAGE for at least this many ms to qualify
  // for cross-store. Clicking checkout EARLIER -> uses the NATIVE CHECKOUT as usual.
  // Restarts from zero ON EVERY page load/navigation (measured per page, not accumulated over the session).
  // 30000 = 30 seconds. SET TO 0 to turn this condition OFF.
  const MIN_TIME_ON_PAGE_MS = 0;

  /* =========================================================================
   * ====  GROUP 3: PRODUCTS  ================================================
   * =======================================================================*/

  /* ===== CONDITION: PRODUCTS LIMITED TO 1 UNIT ==========================
   * Some products often fail when 2 or more units are bought. For the products listed here:
   * the TOTAL QUANTITY of THAT product in the cart must be EXACTLY 1 to go to aldwynco.
   * More than 1 -> clicking checkout uses the current store's NATIVE CHECKOUT as usual.
   *
   * It CAN STILL be bought TOGETHER with OTHER products — this only limits its own quantity.
   * ALL VARIANTS of the same product are added up:
   *    A/Black 1 unit + A/White 1 unit = 2 units of product A  -> REJECTED (native checkout)
   *    A/Black 2 units                 = 2 units of product A  -> REJECTED
   *    A/Black 1 unit + product B      = 1 unit of product A   -> PASSES (any quantity of B is fine)
   * ========================================================================*/

  // Write the product TITLE or HANDLE on the CURRENT STORE (case, Vietnamese diacritics and punctuation
  // are all normalized when matching). Pasting the current store's whole product LINK also works,
  // e.g. 'https://solfootwear.com/products/vintage-ankle-boots' (the script only takes the handle after /products/).
  // LEAVE THE ARRAY EMPTY [] -> condition OFF (any product can be bought in any quantity).
  const SINGLE_UNIT_PRODUCTS = [
    "Slipclouds Orthopedic Shoes",
    "vintage-casual-short-ankle-boots",
    "vintage-ankle-boots"
    // 'Anti-Choking Emergency Rescue Kit',      // match by title
    // 'anti-choking-emergency-rescue-kit',      // or match by handle
  ];

  /* =========================================================================
   * ====  GROUP 4: VISITOR BLOCKING  ========================================
   * =======================================================================*/

  /* ===== TOP-PRIORITY CONDITION: BLOCK BY THE IP'S COUNTRY =================
   * IPs from countries in BLOCKED_COUNTRIES do NOT run cross-store (clicking checkout -> uses the
   * native checkout as usual). This condition is CHECKED BEFORE every other condition (time windows...).
   * The browser does not know the IP's country, so the script asks an IP-geolocation service (with CORS).
   * ========================================================================*/

  // Turn the whole country-blocking feature on/off.
  const GEO_BLOCK_ENABLED = true;

  // Blocked COUNTRY CODES (ISO-2, uppercase). VN=Vietnam, HK=Hong Kong, SG=Singapore.
  const BLOCKED_COUNTRIES = ['VN','HK', 'SG', 'JP', 'CN'];

  // When the country CANNOT be determined (API error/blocked/timeout):
  //  - false (default — SAFE for the top-priority condition): cross-store does NOT run -> native checkout.
  //  - true: still run cross-store without knowing the country (accepting the risk of missing a blocked-country IP).
  const PROCEED_IF_GEO_UNKNOWN = false;

  // Maximum wait for each geolocation API call (ms).
  const GEO_LOOKUP_TIMEOUT_MS = 4000;

  /* ===== CONDITION: BAN SPECIFIC IP ADDRESSES ============================
   * Visitors whose IP is in BLOCKED_IPS do NOT run cross-store (clicking checkout -> native checkout as
   * usual), EXACTLY like a country block. Checked AT THE SAME TIME as the country block and SHARES the
   * single IP lookup (all three geolocation services also return an "ip" field) -> no extra API calls.
   * ========================================================================*/

  // BANNED IP LIST. Write full IPs, one string per IP (IPv4 or IPv6). Matches the EXACT whole
  // address (not ranges/subnets). LEAVE THE ARRAY EMPTY [] -> condition OFF (no IP is banned).
  //   E.g. IPv4: '203.0.113.7'   E.g. IPv6: '2001:db8::1'
  // NOTE: this is a blocklist -> it only blocks when the visitor's IP is KNOWN to be on the list. If the IP
  // can't be looked up, the country condition (fail-closed) decides; nothing is blocked here.
  const BLOCKED_IPS = [
    // 22 IPs (duplicates removed) — taken from the IP list screenshot, all in the US.
    '45.95.186.236',
    '45.141.12.203',
    '74.81.36.101',
    '74.81.46.162',
    '85.31.54.31',
    '104.161.28.205',
    '104.161.41.208',
    '104.161.63.29',
    '107.178.102.204',
    '147.124.205.240',
    '148.163.80.25',
    '173.0.61.155',
    '173.0.61.170',
    '173.0.61.187',
    '193.42.244.58',
    '193.42.244.88',
    '193.42.247.112',
    '193.56.23.78',
    '194.62.165.110',
    '194.62.165.206',
    '194.62.166.86',
    '216.173.116.252',
    '187.15.91.89'
  ];

  /* ===== CONDITION: RUN ON MOBILE DEVICES ONLY ===========================
   * Redirect to aldwynco ONLY when the visitor uses a PHONE (and tablets, depending on the setting below).
   * Desktop/laptop -> clicking checkout uses the current store's NATIVE CHECKOUT as usual.
   * ========================================================================*/

  // Turn this condition on/off. false -> runs on ALL devices (as before this condition was added).
  const MOBILE_ONLY_ENABLED = true;

  // Do tablets (iPad, Android tablet) count as "mobile"?
  //  true  (default): tablets COUNT as mobile -> still go to aldwynco.
  //  false           : ONLY PHONES go; tablets use the native checkout like desktop.
  const TREAT_TABLET_AS_MOBILE = false;

  /* =========================================================================
   * ====  GROUP 5: DOMAIN  ==================================================
   * =======================================================================*/

  // Domain of the TARGET store (where the cart permalink points). This is the only constant
  // to change if the domain changes. No https:// and no trailing /.
  const TARGET_DOMAIN = 'aldwynco.com';

  // The target store's .myshopify.com domain (if known). Used to detect it more reliably
  // when browsing through preview/myshopify. Leave empty if unknown.
  const TARGET_MYSHOPIFY = ''; // e.g. 'aldwynco.myshopify.com'

  // Hosts treated as "ON the target store" -> the script does NOT intervene and lets the native checkout run.
  // TARGET_DOMAIN and its www variant are included automatically. Add staging/preview domains if any.
  const EXTRA_TARGET_HOSTS = [
    // 'staging.aldwynco.com',
    // 'aldwynco.myshopify.com',
  ];

  /* =========================================================================
   * ====  GROUP 6: TEST SWITCHES (turn all off in production)  ==============
   * =======================================================================*/

  // Turns on verbose console logging (info). Warnings/errors are always printed even when this flag is off.
  const DEBUG = false;

  // Force the device type for TESTING: 'mobile' (simulate a phone) or 'desktop'. Leave empty '' in production.
  // On a computer -> set 'mobile' to try the redirect flow. (DevTools device emulation — F12, then click
  // the phone icon — is also detected correctly without editing this line.)
  const DEVICE_TYPE_OVERRIDE = '';

  // Force the country for TESTING (e.g. 'US' to simulate a visitor OUTSIDE the blocked countries; 'VN' to test a block).
  // Leave empty '' in production. NOTE: if you are in VN, you must set 'US' (or use a VPN) to test the redirect.
  const GEO_COUNTRY_OVERRIDE = '';

  // Force the IP for TESTING (simulate a visitor with a specific IP, e.g. one in BLOCKED_IPS to test a block).
  // Leave empty '' in production.
  // TEST TIP: when forcing the IP WITHOUT forcing the country, the script treats it as "country unknown" -> if
  // GEO_BLOCK_ENABLED is on and PROCEED_IF_GEO_UNKNOWN=false, the visitor is blocked by the country condition (you
  // can't tell whether the IP or the country blocked it). To test only the IP ban, also set GEO_COUNTRY_OVERRIDE='US' (or turn off GEO_BLOCK_ENABLED).
  const GEO_IP_OVERRIDE = '';

  /* =========================================================================
   * ====  GROUP 7: TECHNICAL (rarely needs editing)  ========================
   * =======================================================================*/

  // ---- Permalink ----------------------------------------------------------

  // If true -> the permalink gets ?storefront=true (lands on the CART page). Default false -> straight to CHECKOUT.
  const GO_TO_CART_INSTEAD = false;

  // Maximum permalink URL length (safe for browsers). Longer -> native checkout.
  const MAX_URL_LENGTH = 2000;

  // ---- Loading data from the target store ---------------------------------

  // Time limit for each cross-origin request (ms). Exceeded -> treated as a failure -> native checkout.
  const REQUEST_TIMEOUT_MS = 8000;

  // Lifetime of the catalogue cache in sessionStorage (ms).
  const CATALOGUE_TTL_MS = 10 * 60 * 1000; // 10 minutes

  // Prevent endless scanning: 40 pages * 250 products = at most 10,000 products.
  const MAX_CATALOGUE_PAGES = 40;
  const PAGE_DELAY_MS = 200; // pause between pages to avoid being throttled

  // Maximum attempts for EACH catalogue page when throttled (429/430) before giving up.
  const MAX_THROTTLE_RETRIES = 3;

  // ---- Fallback mode (mode B) ---------------------------------------------

  // FALLBACK MODE (mode B) when the cross-origin fetch FAILS (CORS blocked / network error):
  // ENCODE the cart into the URL '/cart?aldwyn_xcart=...' and go to aldwynco so this same script
  // (running on aldwynco) decodes it and matches SAME-ORIGIN.
  // SET TO false (as requested): the aldwyn_xcart URL is NEVER used anymore. When a direct permalink can't be
  // built (variants don't match 100%, OR the cross-origin fetch fails) -> USE the current store's NATIVE
  // CHECKOUT as usual. Only a full match goes straight to https://aldwynco.com/cart/ID:QTY.
  const ENABLE_DATA_REDIRECT_FALLBACK = false;

  // Name of the URL parameter carrying the cart data to the target store (the "receiving" side reads it).
  const INCOMING_PARAM = 'aldwyn_xcart';

  // "Landing" page on the target store for the fallback mode (should be light; the script then jumps
  // on to /cart/ID:QTY once matching is done). '/cart' is usually light and fits the context.
  const RECEIVER_LANDING_PATH = '/cart';

  /* =========================================================================
   * ====  END OF CONFIGURATION — logic below  ===============================
   * =======================================================================*/

  const TARGET_BASE = 'https://' + TARGET_DOMAIN;
  const CHECKOUT_SELECTOR = 'button[name="checkout"]';
  const SESSION_KEY = 'aldwyn_catalogue_cache_v2'; // v2: cached products also carry their id
  const GEO_SESSION_KEY = 'aldwyn_geo_country_v1';
  const BYPASS_ATTR = 'aldwynBypass'; // dataset key: marks a submit that "skips the interceptor"

  // Normalized blocked-country list (uppercase ISO-2) for fast matching.
  const NORM_BLOCKED_COUNTRIES = (Array.isArray(BLOCKED_COUNTRIES) ? BLOCKED_COUNTRIES : [])
    .map((c) => String(c).trim().toUpperCase());

  // Normalize an IP for matching: trim spaces, lowercase (IPv6 has hex letters), strip surrounding '[]' and
  // an IPv4 ':port'. Does NOT compress/expand IPv6 -> paste IPs exactly as the geolocation service shows them.
  const normalizeIp = (ip) => {
    if (ip === null || ip === undefined) return '';
    let s = String(ip).trim().toLowerCase();
    if (s.charAt(0) === '[') {                       // form '[IPv6]' or '[IPv6]:port'
      const end = s.indexOf(']');
      if (end !== -1) s = s.slice(1, end);
    } else if ((s.match(/:/g) || []).length === 1) { // exactly 1 ':' = IPv4:port -> drop the port
      s = s.slice(0, s.indexOf(':'));
    }
    if (s.indexOf('::ffff:') === 0 && s.indexOf('.') !== -1) s = s.slice(7); // IPv4-mapped IPv6
    return s;
  };

  // Normalized banned-IP list. Empty -> IP banning is off.
  const NORM_BLOCKED_IPS = (Array.isArray(BLOCKED_IPS) ? BLOCKED_IPS : [])
    .map((ip) => normalizeIp(ip)).filter(Boolean);

  // Is banning specific IPs on? (only when the list has at least 1 IP)
  const IP_BLOCK_ENABLED = NORM_BLOCKED_IPS.length > 0;

  // Is a network IP lookup needed? Yes if blocking by country OR banning IPs.
  const NETWORK_GATE_ENABLED = GEO_BLOCK_ENABLED || IP_BLOCK_ENABLED;

  // In-memory caches (live for the page's lifetime)
  const productCache = new Map();   // handle -> product | null (finished results)
  const productInFlight = new Map();// handle -> Promise (in flight) — merges requests for the SAME handle
  let catalogueMemo = null;         // array of (trimmed) products | null
  let cataloguePromise = null;      // single-flight: the catalogue load in flight
  let inFlight = false;             // double-click guard flag

  // ---- Log ----------------------------------------------------------------
  const log = (level, ...args) => {
    const prefix = '[aldwyn-xstore]';
    if (level === 'error') { console.error(prefix, ...args); return; }
    if (level === 'warn') { console.warn(prefix, ...args); return; }
    if (DEBUG) console.info(prefix, ...args);
  };

  // ---- Utilities ----------------------------------------------------------
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // Normalize a string for matching: lowercase, strip Vietnamese diacritics, drop punctuation, collapse spaces.
  const normalize = (str) => {
    if (str === null || str === undefined) return '';
    return String(str)
      .toLowerCase()
      .replace(/đ/g, 'd')               // 'đ' is not decomposed by NFD -> handled separately
      .normalize('NFD')                 // split letters from their marks
      .replace(/[̀-ͯ]/g, '')  // remove the marks (diacritics)
      .replace(/[^a-z0-9]+/g, ' ')      // punctuation -> space
      .trim()
      .replace(/\s+/g, ' ');            // collapse repeated spaces
  };

  // Allows whole product LINKS in the config: 'https://solfootwear.com/products/vintage-ankle-boots?variant=1'
  // -> takes only the handle 'vintage-ankle-boots' (drops the domain, ?variant=..., trailing /). Not a link -> unchanged.
  const handleFromUrl = (value) => {
    const m = String(value === null || value === undefined ? '' : value).match(/\/products\/([^/?#]+)/);
    if (!m) return value;
    try { return decodeURIComponent(m[1]); } catch (e) { return m[1]; }
  };

  // Build a copy of the map with NORMALIZED KEYS (lookups ignore case/diacritics). A link key -> its handle.
  const buildNormalizedMap = (map) => {
    const out = {};
    for (const key in map) {
      if (Object.prototype.hasOwnProperty.call(map, key)) {
        out[normalize(handleFromUrl(key))] = map[key];
      }
    }
    return out;
  };

  const NORM_PRODUCT_CAPS = buildNormalizedMap(MAX_DAILY_ORDERS_PER_PRODUCT);

  // ---- Store detection ----------------------------------------------------
  // Returns true when RUNNING on the target store itself -> then the script does NOT intervene.
  const isTargetStore = () => {
    const host = (location.hostname || '').toLowerCase().replace(/^www\./, '');
    if (host === TARGET_DOMAIN) return true;
    if (TARGET_MYSHOPIFY && host === TARGET_MYSHOPIFY.toLowerCase()) return true;

    // Shopify.shop (the permanent domain) is injected by Shopify at runtime via content_for_header.
    try {
      if (TARGET_MYSHOPIFY && window.Shopify && Shopify.shop &&
        String(Shopify.shop).toLowerCase() === TARGET_MYSHOPIFY.toLowerCase()) {
        return true;
      }
    } catch (e) { /* ignore */ }

    for (let i = 0; i < EXTRA_TARGET_HOSTS.length; i++) {
      const h = String(EXTRA_TARGET_HOSTS[i]).toLowerCase().replace(/^www\./, '');
      if (host === h) return true;
    }
    return false;
  };

  // ---- fetch with a timeout (covering body reading TOO) -------------------
  // The timer stays alive while the body is read (res.json()), so a response that has sent
  // its headers but HANGS mid-body is also aborted -> reject -> back to native checkout.
  // Returns { status, ok, headers, data } — data is the parsed JSON (only when ok).
  const fetchWithTimeout = async (url, options) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      const res = await fetch(url, Object.assign({ signal: controller.signal }, options || {}));
      const data = res.ok ? await res.json() : null; // read the body WHILE the timer is still running
      return { status: res.status, ok: res.ok, headers: res.headers, data: data };
    } finally {
      clearTimeout(timer);
    }
  };

  // ---- Read the current cart (same-origin -> always CORS-safe) ------------
  // Goes through fetchWithTimeout so a hanging /cart.js is aborted too -> reject -> back to native checkout.
  const fetchCurrentCart = async () => {
    const res = await fetchWithTimeout('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('/cart.js -> ' + res.status);
    return res.data;
  };

  // ---- Fetch a target product by handle: /products/HANDLE.json -----------
  // NOTE: uses .json (public storefront feed, HAS CORS: Access-Control-Allow-Origin: *)
  // and NOT .js (Ajax API — does not reliably allow cross-origin CORS).
  // - 404  -> returns null (not found; the caller will try another strategy).
  // - throw (CORS/network) -> propagates up -> CANCEL the redirect, use native checkout (decision 4).
  const fetchProductByHandle = (handle) => {
    if (!handle) return Promise.resolve(null);
    if (productCache.has(handle)) return Promise.resolve(productCache.get(handle));
    // A request for this EXACT handle is already running -> share it (when resolving lines IN PARALLEL,
    // two lines of the same product won't fetch twice).
    if (productInFlight.has(handle)) return productInFlight.get(handle);

    const p = (async () => {
      const url = TARGET_BASE + '/products/' + encodeURIComponent(handle) + '.json';
      // Retries are CAPPED when throttled (429/430) — common when resolving many lines IN PARALLEL
      // (all fired at once). Retry (like pullCatalogue) so we DON'T fall back to native checkout just because of throttling.
      for (let attempts = 1; ; attempts++) {
        const res = await fetchWithTimeout(url, { headers: { Accept: 'application/json' } });

        if (res.status === 429 || res.status === 430) {
          if (attempts >= MAX_THROTTLE_RETRIES) {
            throw new Error('products/' + handle + '.json throttled (' + res.status +
              ') after ' + attempts + ' attempts');
          }
          const retry = Math.min((Number(res.headers.get('Retry-After')) || 2) * 1000, 3000);
          await delay(retry);
          continue; // retry this same handle (capped)
        }
        if (res.status === 404) { productCache.set(handle, null); return null; }
        if (!res.ok) throw new Error('products/' + handle + '.json -> ' + res.status);

        const data = res.data;
        const product = data && data.product ? data.product : null;
        productCache.set(handle, product);
        return product;
      }
    })();
    productInFlight.set(handle, p);
    // When done (success OR error), remove it from the in-flight list: after an error the next call can retry.
    const done = () => productInFlight.delete(handle);
    p.then(done, done);
    return p;
  };

  // Trim a product down to the fields needed for variant matching (saves memory/sessionStorage).
  const extractOptionNames = (product) => {
    const opts = product && product.options;
    if (!Array.isArray(opts)) return [];
    // product.options can be an array of objects {name,...} (.json feed) or an array of strings.
    return opts.map((o) => (o && typeof o === 'object' ? o.name : o));
  };

  const trimProduct = (p) => ({
    id: p.id,
    handle: p.handle,
    title: p.title,
    options: extractOptionNames(p),
    variants: (Array.isArray(p.variants) ? p.variants : []).map((v) => ({
      id: v.id,
      option1: v.option1 !== undefined ? v.option1 : null,
      option2: v.option2 !== undefined ? v.option2 : null,
      option3: v.option3 !== undefined ? v.option3 : null,
      available: v.available,
    })),
  });

  // ---- Scan the whole target catalogue (public feed /products.json, page-until-empty) --
  const pullCatalogue = async () => {
    const all = [];
    for (let page = 1; page <= MAX_CATALOGUE_PAGES; page++) {
      const url = TARGET_BASE + '/products.json?limit=250&page=' + page;

      // Load 1 page, with a CAPPED number of retries when throttled (429/430) so checkout does NOT hang.
      let data = null;
      for (let attempts = 1; ; attempts++) {
        const res = await fetchWithTimeout(url, { headers: { Accept: 'application/json' } });

        if (res.status === 429 || res.status === 430) { // throttled
          if (attempts >= MAX_THROTTLE_RETRIES) {
            // Out of attempts -> THROW so the interceptor's .catch() falls back to native checkout (no endless loop).
            throw new Error('products.json page ' + page + ' throttled (' + res.status +
              ') after ' + attempts + ' attempts');
          }
          // Retry-After is NOT a CORS-safelisted header -> usually null cross-origin (=> 2s).
          // Cap the wait so a large value can't hang checkout either.
          const retry = Math.min((Number(res.headers.get('Retry-After')) || 2) * 1000, 3000);
          await delay(retry);
          continue; // retry THIS same page (capped)
        }
        if (!res.ok) throw new Error('products.json page ' + page + ' -> ' + res.status);

        data = res.data;
        break; // page loaded -> leave the retry loop
      }

      const products = data && Array.isArray(data.products) ? data.products : [];
      if (products.length === 0) break;            // empty array = end of catalogue
      for (let i = 0; i < products.length; i++) all.push(trimProduct(products[i]));
      if (products.length < 250) break;            // last page (not full)
      await delay(PAGE_DELAY_MS);
    }
    return all;
  };

  // Get the catalogue with caching: memory -> sessionStorage (TTL) -> fresh download.
  // Single-flight: when resolving many lines IN PARALLEL, they all SHARE 1 download (no duplicate downloads).
  const getCatalogue = () => {
    if (catalogueMemo) return Promise.resolve(catalogueMemo);
    if (cataloguePromise) return cataloguePromise;

    cataloguePromise = (async () => {
      try {
        const raw = sessionStorage.getItem(SESSION_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && Array.isArray(parsed.products) &&
            (Date.now() - parsed.t) < CATALOGUE_TTL_MS) {
            catalogueMemo = parsed.products;
            log('info', 'Using catalogue from sessionStorage:', catalogueMemo.length, 'products');
            return catalogueMemo;
          }
        }
      } catch (e) { /* sessionStorage broken/unavailable -> ignore */ }

      log('info', 'Loading catalogue from', TARGET_BASE);
      const products = await pullCatalogue();
      catalogueMemo = products;
      try {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify({ t: Date.now(), products }));
      } catch (e) { /* over quota -> memory cache only */ }
      log('info', 'Catalogue loaded:', products.length, 'products');
      return products;
    })();
    // When done, clear the in-flight flag: on success catalogueMemo is set; on error the next click reloads.
    const clear = () => { cataloguePromise = null; };
    cataloguePromise.then(clear, clear);
    return cataloguePromise;
  };

  // ---- Variant matching ---------------------------------------------------
  const isAvailable = (variant) => variant && variant.available !== false;

  // Get the normalized {name,value} pairs from 1 /cart.js line item.
  const extractCartPairs = (item) => {
    const owv = Array.isArray(item.options_with_values) ? item.options_with_values : [];
    return owv.map((o) => ({
      name: normalize(o.name),
      value: normalize(o.value),
    }));
  };

  // A product with only 1 variant ("Default Title") -> no options to match.
  const isDefaultOnly = (cartPairs) =>
    cartPairs.length === 0 ||
    (cartPairs.length === 1 && cartPairs[0].name === 'title' && cartPairs[0].value === 'default title');

  // For default-only products: pick the ONLY available variant; if ambiguous -> null.
  const pickForDefault = (variants) => {
    if (variants.length === 1) return isAvailable(variants[0]) ? variants[0] : null;
    const avail = variants.filter(isAvailable);
    if (avail.length === 1) return avail[0];
    return null; // several choices -> don't guess
  };

  // (a) Match by option NAME: requires the same set of option names and EVERY value equal.
  // If the target product has a DIFFERENT number of options -> different name sets -> no match (safe).
  const variantMatchesByName = (variant, targetOptionNames, cartPairs) => {
    const targetMap = {};
    for (let i = 0; i < targetOptionNames.length; i++) {
      const val = variant['option' + (i + 1)];
      if (val === null || val === undefined) continue;
      targetMap[normalize(targetOptionNames[i])] = normalize(val);
    }
    const targetKeys = Object.keys(targetMap);
    if (targetKeys.length !== cartPairs.length) return false;      // different number of options -> skip
    return cartPairs.every((pair) => targetMap[pair.name] === pair.value);
  };

  // Returns the matching, in-stock target variant, or null (out of stock / no match).
  // NOTE: /products.json only gives a boolean `available`, NO stock count, so
  // "quantity > stock" can't be checked up front; we only require available !== false,
  // and checkout is the final source of truth.
  //
  // Matches by option NAME ONLY. There is NO fallback by POSITION:
  // the two stores may declare options in a different ORDER, so matching by index can easily pick
  // the WRONG variant (e.g. Left/Right swapped, symmetric sizes). If the names can't be aligned
  // -> return null to CANCEL the redirect and use native checkout (fail-safe) instead of guessing by position.
  const matchVariant = (product, item) => {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    if (variants.length === 0) return null;

    const cartPairs = extractCartPairs(item);
    if (isDefaultOnly(cartPairs)) return pickForDefault(variants);

    const targetOptionNames = extractOptionNames(product);

    const byName = variants.find((v) => variantMatchesByName(v, targetOptionNames, cartPairs));
    if (byName) return isAvailable(byName) ? byName : null;

    return null;
  };

  // ---- Resolve 1 line item -> variant id on the target store -------------

  // Line items that CANNOT be recreated through a cart permalink -> must CANCEL (decision 4):
  //  - subscription/selling plan (the permalink doesn't carry selling_plan),
  //  - custom properties entered by the customer (engraving/upload/gift...).
  const isPermalinkSafe = (item) => {
    if (item.selling_plan_allocation) {
      log('warn', 'Line item has a selling plan (subscription) — cannot be carried through a permalink:', item.title);
      return false;
    }
    if (item.properties) {
      const custom = Object.keys(item.properties).filter((k) => k.charAt(0) !== '_');
      if (custom.length > 0) {
        log('warn', 'Line item has custom properties — cannot be carried through a permalink:', item.title, custom);
        return false;
      }
    }
    return true;
  };

  // Try to match a variant within ONE product. Returns { product, variant } if matched + in stock, else null.
  const matchInProduct = (product, item) => {
    const variant = matchVariant(product, item);
    return variant ? { product: product, variant: variant } : null;
  };

  // Resolve 1 line item -> { product, variant } on the target store, or null if there is no match.
  // Strategies (in priority order, top down):
  //  (1) FAST PATH: guess by handle; if handle + title + VARIANT all match -> use it right away
  //      (no need to load the whole catalogue). This is the most common case.
  //  (2) SCAN products with the same title: when (1) finds a product but the variant does NOT match (quite
  //      likely the right product is ANOTHER product with the same name, e.g. aldwynco has 'anti-choking-emergency'
  //      AND 'anti-choking-emergency-1'), go through the catalogue products with the same title (if some have the
  //      EXACT same title, only those are considered) and stop at the FIRST one with a matching variant. 0 matches -> cancel.
  const resolveLineItem = async (item) => {
    if (!isPermalinkSafe(item)) return null;

    // (1) Fast path: guess by handle. Handle + title + variant all match -> use it right away.
    if (item.handle) {
      const guess = await fetchProductByHandle(item.handle); // 404 -> null; CORS -> throw -> cancel everything
      if (guess && normalize(guess.title) === normalize(item.product_title)) {
        const m = matchInProduct(guess, item);
        if (m) return m;
        // Handle + title match but the variant does NOT -> the right product may be ANOTHER product
        // with the same title -> fall through to the scan below.
        log('info', 'Handle+title match but the variant does not -> scanning products with the same title');
      }
    }

    // (2) Scan the products with the same title. Go through them in order and STOP AT the FIRST product whose
    //     variant matches -> check out that product (no further scanning). If none match -> cancel (native checkout).
    const catalogue = await getCatalogue();
    const target = normalize(item.product_title);
    const sameTitle = target ? catalogue.filter((p) => normalize(p.title) === target) : [];
    if (sameTitle.length === 0) {
      log('warn', 'No product with the same title found on ' + TARGET_DOMAIN + ':',
        item.product_title, '(handle:', item.handle + ')');
      return null;
    }

    // Several products with the same title AFTER normalization (e.g. differing only by a final dot "...Boots" /
    // "...Boots.") are DIFFERENT products: if some have the EXACT same title, ONLY those are considered. Without this
    // step, whichever product was edited last on aldwyn (the catalogue lists the most recently edited first) would take the other link's orders.
    const exactTitle = String(item.product_title || '').trim();
    const exact = sameTitle.filter((p) => String(p.title || '').trim() === exactTitle);
    const candidates = exact.length > 0 ? exact : sameTitle;

    for (let i = 0; i < candidates.length; i++) {
      const m = matchInProduct(candidates[i], item);
      if (m) {
        log('info', 'First same-title product with a matching variant -> checkout:', m.product.handle);
        return m; // STOP right at the first matching product
      }
    }

    log('warn', 'Found ' + sameTitle.length + ' products with the same title but NONE has a matching variant:',
      item.product_title, item.options_with_values);
    return null;
  };

  // ---- Build the permalink ------------------------------------------------
  // Keep ':' and ',' AS IS (no percent-encoding), as the cart permalink syntax requires.
  const buildPermalink = (resolved) => {
    const path = resolved.map((r) => r.variantId + ':' + r.quantity).join(',');
    let url = TARGET_BASE + '/cart/' + path;
    if (GO_TO_CART_INSTEAD) url += '?storefront=true';
    return url;
  };

  // ---- Encode/decode the cart for the fallback mode (redirect-with-data) ---
  // Base64url over a UTF-8 string (via TextEncoder/TextDecoder) -> safe for Vietnamese text,
  // more compact than encodeURIComponent for accented characters, and safe inside a query string.
  const toBase64Url = (bytes) => {
    let bin = '';
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  };
  const fromBase64Url = (str) => {
    const b64 = str.replace(/-/g, '+').replace(/_/g, '/');
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes;
  };

  // Carries only the minimum fields the target store needs to re-match: title, handle, quantity, options.
  const encodeCartPayload = (items) => {
    const payload = items.map((it) => ({
      t: it.product_title,
      h: it.handle,
      // Reconcile quantities still pending in the DOM (like the main permalink path at resolved.push
      // using resolveQuantity), so quantity edits not yet POSTed (300ms debounce) are NOT
      // lost on the fallback path. resolveQuantity falls back to it.quantity when no input matches.
      q: resolveQuantity(it),
      o: (Array.isArray(it.options_with_values) ? it.options_with_values : [])
        .map((o) => [o.name, o.value]),
    }));
    const json = JSON.stringify(payload);
    return toBase64Url(new TextEncoder().encode(json));
  };

  // Decode -> an array of "pseudo line items" with EXACTLY the shape resolveLineItem needs.
  const decodeCartPayload = (param) => {
    const json = new TextDecoder().decode(fromBase64Url(param));
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) throw new Error('payload is not an array');
    return arr.map((it) => ({
      product_title: it.t,
      handle: it.h,
      quantity: it.q,
      options_with_values: (Array.isArray(it.o) ? it.o : [])
        .map((pair) => ({ name: pair[0], value: pair[1] })),
    }));
  };

  // Source store: CANNOT reach the target store (CORS/network) -> encode the cart and go to the target store.
  // Returns true if it navigated (leaving the page), false if unusable (the caller falls back to native checkout).
  const redirectWithCartData = (cart) => {
    if (!ENABLE_DATA_REDIRECT_FALLBACK) return false;
    try {
      const encoded = encodeCartPayload(cart.items);
      const sep = RECEIVER_LANDING_PATH.indexOf('?') === -1 ? '?' : '&';
      const url = TARGET_BASE + RECEIVER_LANDING_PATH + sep + INCOMING_PARAM + '=' + encoded;
      if (url.length > MAX_URL_LENGTH) {
        log('warn', 'Fallback URL too long (' + url.length + ') -> native checkout');
        return false;
      }
      log('info', 'Cross-origin fetch failed -> redirecting with the cart data to', TARGET_BASE);
      window.location.assign(url);
      return true;
    } catch (e) {
      log('error', 'Error building the redirect-with-data -> native checkout:', e);
      return false;
    }
  };

  // ---- Button state (the theme's NATIVE loading animation) ---------------
  // The theme's NATIVE spinner SVG (snippet 'thb-loading' in svg-icons.liquid). The native checkout button only
  // has <span>text</span> and no spinner; we insert this span.loading-overlay so that adding
  // the 'loading' class shows EXACTLY the same spinner animation as other buttons (Add to cart...). No full-screen
  // overlay, no "Checking out..." text — just the button's normal animation.
  const SPINNER_SVG = '<svg aria-hidden="true" focusable="false" role="presentation" ' +
    'class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">' +
    '<circle class="spinner-path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg>';

  // ---- Native checkout fallback -------------------------------------------

  // Show the busy state (NOT actually disabled: the button is still needed as the submitter for requestSubmit).
  const setBusy = (button, busy) => {
    try {
      if (busy) {
        // Insert the theme's NATIVE spinner into the button (only once) to show the usual loading animation.
        if (!button.querySelector('.loading-overlay')) {
          const ov = document.createElement('span');
          ov.className = 'loading-overlay';
          ov.innerHTML = SPINNER_SVG;
          button.appendChild(ov);
        }
        button.setAttribute('aria-busy', 'true');
        button.classList.add('loading');
      } else {
        button.removeAttribute('aria-busy');
        button.classList.remove('loading');
      }
    } catch (e) { /* ignore DOM errors */ }
  };

  // Let the NATIVE checkout run after we called preventDefault. The submit must be TRIGGERED AGAIN manually.
  const fallbackToNativeCheckout = (button) => {
    inFlight = false;
    setBusy(button, false); // remove the loading state from the button

    // button.form resolves BOTH the wrapping form (drawer) AND a link via form="cart-form" (cart page).
    const form = button.form;

    // Why requestSubmit(button) INSTEAD OF form.submit():
    //  - requestSubmit(submitter) SENDS the submit button's name/value (name="checkout") — this is exactly
    //    the signal for Shopify to go to the checkout page. form.submit() does NOT include the submitter, so it
    //    loses "checkout" and only updates the cart, WITHOUT going to checkout.
    //  - requestSubmit fires a 'submit' event (not 'click'), while our interceptor listens for 'click',
    //    so there is NO re-entry -> no endless loop.
    if (form && typeof form.requestSubmit === 'function') {
      try {
        button.dataset[BYPASS_ATTR] = 'true'; // extra safeguard in case a click gets re-dispatched
        form.requestSubmit(button);
        setTimeout(() => { try { delete button.dataset[BYPASS_ATTR]; } catch (e) { } }, 0);
        return;
      } catch (e) {
        log('warn', 'requestSubmit(button) failed, trying a native click:', e);
      }
    }

    // Last resort: replay the native click with the bypass flag so the interceptor SKIPS it (no loop).
    try {
      button.dataset[BYPASS_ATTR] = 'true';
      button.click();
      setTimeout(() => { try { delete button.dataset[BYPASS_ATTR]; } catch (e) { } }, 0);
    } catch (e) {
      log('error', 'Could not fall back to native checkout:', e);
    }
  };

  // ---- Actual quantity (reconciling pending edits on the /cart page) -------
  // The +/- buttons and the quantity input only POST to the server AFTER a 300ms debounce, so /cart.js
  // may return an OLD quantity if the customer just edited it and clicked checkout right away. Prefer reading
  // the value currently shown in the matching updates[] input (just as the native checkout submits
  // #cart-form with the quantity just entered). Missing/invalid -> use /cart.js.
  const resolveQuantity = (item) => {
    try {
      if (item && item.key) {
        const wantedId = 'updates_' + item.key;
        const inputs = document.querySelectorAll('input[name="updates[]"]');
        let picked = null;
        for (let i = 0; i < inputs.length; i++) {
          if (inputs[i].id !== wantedId) continue; // compare ids in JS -> no need to escape the key's ':'
          picked = inputs[i];
          if (inputs[i].offsetParent !== null) break; // prefer the VISIBLE input (mobile/desktop)
        }
        if (picked) {
          const domQty = parseInt(picked.value, 10);
          if (Number.isFinite(domQty) && domQty > 0) return domQty;
        }
      }
    } catch (e) { /* ignore DOM errors -> use the quantity from /cart.js */ }
    return item.quantity;
  };

  // ---- "Buy only 1 unit" rule (SINGLE_UNIT_PRODUCTS) ----------------------
  // Normalize the list ONCE so matching ignores case, diacritics and punctuation. A link -> its handle.
  const NORM_SINGLE_UNIT_PRODUCTS = (Array.isArray(SINGLE_UNIT_PRODUCTS) ? SINGLE_UNIT_PRODUCTS : [])
    .map((p) => normalize(handleFromUrl(p))).filter(Boolean);

  // Is this cart line on the "only 1 unit" list?
  // Matches by HANDLE or TITLE -> either form works in the config.
  const isSingleUnitItem = (item) =>
    NORM_SINGLE_UNIT_PRODUCTS.indexOf(normalize(item && item.handle)) !== -1 ||
    NORM_SINGLE_UNIT_PRODUCTS.indexOf(normalize(item && item.product_title)) !== -1;

  // Key that GROUPS the variants of the SAME product together (A/Black and A/White share a key).
  // Prefers the handle since it is the same for all variants and more stable than the title. null = not on the list.
  const singleUnitGroupKey = (item) => {
    if (!isSingleUnitItem(item)) return null;
    return normalize(item && item.handle) || normalize(item && item.product_title);
  };

  // Returns { item, units } of the FIRST offending product (total quantity > 1), or null if the cart is valid.
  // Uses resolveQuantity like the permalink builder -> quantities just edited on the /cart page (not yet
  // POSTed because of the 300ms debounce) are still counted CORRECTLY.
  const findSingleUnitViolation = (items) => {
    if (NORM_SINGLE_UNIT_PRODUCTS.length === 0) return null;   // condition off
    if (!Array.isArray(items) || items.length === 0) return null;

    const unitsByKey = {};   // group key -> TOTAL quantity (sum of all variants)
    const firstItemByKey = {}; // group key -> first line (only for the log name)
    const keysInOrder = [];  // keeps the order of appearance -> stable reporting, independent of object key order

    for (let i = 0; i < items.length; i++) {
      const key = singleUnitGroupKey(items[i]);
      if (!key) continue;
      if (!Object.prototype.hasOwnProperty.call(unitsByKey, key)) {
        unitsByKey[key] = 0;
        firstItemByKey[key] = items[i];
        keysInOrder.push(key);
      }
      const q = resolveQuantity(items[i]);
      unitsByKey[key] += (Number.isFinite(q) && q > 0) ? q : 0;
    }

    for (let i = 0; i < keysInOrder.length; i++) {
      const key = keysInOrder[i];
      if (unitsByKey[key] > 1) return { item: firstItemByKey[key], units: unitsByKey[key] };
    }
    return null;
  };

  // ---- Main flow ----------------------------------------------------------
  const handleCheckout = async (button) => {
    // TOP-PRIORITY CONDITION: settle IP + country (AWAIT if the lookup wasn't finished at click time).
    // A banned IP, an IP from a blocked country, or an unknown country (when PROCEED_IF_GEO_UNKNOWN=false)
    // -> native checkout.
    if (NETWORK_GATE_ENABLED) {
      const net = await resolveVisitorNetwork();
      if (!isVisitorNetworkAllowed(net.country, net.ip)) {
        log('info', 'IP is banned or its country is blocked/unknown (ip=' + net.ip +
          ', country=' + net.country + ') -> native checkout');
        return fallbackToNativeCheckout(button);
      }
    }

    // DAILY CAPS (alongside geo): aldwyn's ORDER COUNT today is over a threshold -> native checkout.
    if (DAILY_LIMIT_ENABLED && ORDER_TOTAL_ENDPOINT) {
      const stats = await getDailyStats();
      if (!isUnderDailyLimits(stats)) {
        log('info', 'Aldwyn is over a daily threshold (' + describeDailyStats(stats) + ') -> native checkout');
        return fallbackToNativeCheckout(button);
      }
    }

    const cart = await fetchCurrentCart();

    if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) {
      log('warn', 'Cart is empty -> using native checkout');
      return fallbackToNativeCheckout(button);
    }

    // PRODUCTS LIMITED TO 1 UNIT: a product in SINGLE_UNIT_PRODUCTS with a total quantity > 1 (even when
    // spread over several variants) -> do NOT go to aldwynco (avoids exactly the failing case), use native checkout.
    const singleUnitViolation = findSingleUnitViolation(cart.items);
    if (singleUnitViolation) {
      const v = singleUnitViolation;
      log('info', 'Product "' + (v.item.product_title || v.item.handle) + '" is limited to 1 unit, ' +
        'but the cart has ' + v.units + ' units -> native checkout');
      return fallbackToNativeCheckout(button);
    }

    // Any line that CANNOT be carried through a permalink (subscription / custom properties)? -> native checkout
    // right away, without trying cross-store (and without sending the customer to the target store to get stuck there).
    for (let i = 0; i < cart.items.length; i++) {
      if (!isPermalinkSafe(cart.items[i])) {
        log('warn', 'Cart has a line that cannot be carried through a permalink -> native checkout:', cart.items[i].title);
        return fallbackToNativeCheckout(button);
      }
    }

    // Resolve ALL lines IN PARALLEL to cut the wait when the cart has many products (each line is
    // a chain of fetches to the target store; run in parallel instead of one after another). The error priority
    // STAYS the same as the sequential version: results are walked in the EXACT line order and we act RIGHT AT the
    // FIRST failing line. Just ONE failing line -> CANCEL everything (no partial redirect).
    const settled = await Promise.allSettled(cart.items.map((it) => resolveLineItem(it)));
    const resolved = [];
    for (let i = 0; i < settled.length; i++) {
      const item = cart.items[i];
      const s = settled[i];

      if (s.status === 'rejected') {
        const err = s.reason;
        // Classify the error. ONLY when fetch truly REJECTS — broken network / CORS block, the browser throws
        // TypeError — is the target store treated as "UNREACHABLE" and redirect-with-data tried (since only
        // same-origin on the target store can re-match — exactly the CORS/network intent in the config section).
        // EVERY OTHER error — readable HTTP 4xx/5xx, 429/430 throttling out of retries, a 200 body that is NOT
        // JSON (password/maintenance/challenge page), or timeout/abort — PROVES the target store STILL
        // responds (server/IP-based, not origin-based). Redirecting there would just hit the same error
        // when matching same-origin and strand the customer on an EMPTY cart page. -> native checkout on the source store.
        if (err instanceof TypeError) {
          log('warn', 'Cannot reach ' + TARGET_DOMAIN + ' (CORS/network):', err);
          if (redirectWithCartData(cart)) return; // already navigating away
          return fallbackToNativeCheckout(button); // fallback off/unusable -> native checkout
        }
        log('warn', 'Target store responded with an error (HTTP/parse/timeout), not CORS/network ' +
          '-> native checkout:', err);
        return fallbackToNativeCheckout(button);
      }

      const match = s.value;
      if (!match) {
        // Reached the target store but NO product/variant match -> native checkout (decision 4).
        // The redirect-with-data fallback is useless too since it uses the same data -> not tried.
        log('warn', 'A line could not be resolved -> redirect CANCELLED, using native checkout:', item.title);
        return fallbackToNativeCheckout(button);
      }
      resolved.push({
        variantId: match.variant.id, quantity: resolveQuantity(item), item: item, product: match.product,
      });
    }

    // PER-PRODUCT CAP: the TARGET product (and its product id for the order count) is only known after matching,
    // so it is checked here. Just ONE product at its cap -> the WHOLE CART uses native checkout (no partial redirect).
    if (DAILY_LIMIT_ENABLED && ORDER_TOTAL_ENDPOINT) {
      const capped = findCappedProduct(resolved, await getDailyStats());
      if (capped) {
        log('info', 'Product "' + capped.product.handle + '" has reached its own cap (' + capped.orders + '/' +
          capped.cap + ') -> native checkout');
        return fallbackToNativeCheckout(button);
      }
    }

    const permalink = buildPermalink(resolved);
    if (permalink.length > MAX_URL_LENGTH) {
      log('warn', 'Permalink too long (' + permalink.length + ' characters) -> using native checkout');
      return fallbackToNativeCheckout(button);
    }

    log('info', 'Cross-store redirect to:', permalink);
    // Don't reset inFlight here: the page is about to navigate away.
    window.location.assign(permalink);
  };

  // ---- "RECEIVING" side (runs when this script is on the target store aldwynco ITSELF) --
  // When the source store uses the fallback mode, it goes to aldwynco with an encoded cart parameter.
  // Here (same-origin) we decode, re-match, then jump to /cart/ID:QTY. Any failure -> STAY on the current
  // page (no forced navigation) to avoid loops. Navigates ONLY after a full match.
  const handleIncomingCart = async (param) => {
    try {
      // The TOP-PRIORITY CONDITION also applies on the receiving side (defensive): banned IP / blocked country -> stay on the page.
      if (NETWORK_GATE_ENABLED) {
        const net = await resolveVisitorNetwork();
        if (!isVisitorNetworkAllowed(net.country, net.ip)) {
          log('info', 'IP is banned or its country is blocked -> incoming cart not processed');
          return;
        }
      }

      // The DEVICE GATE also applies on the receiving side (defensive): not mobile -> stay on the page.
      if (!isDeviceAllowed()) {
        log('info', 'Device is not mobile -> incoming cart not processed');
        return;
      }

      const items = decodeCartPayload(param);
      if (!items.length) { log('warn', 'Empty cart data from the source store'); return; }

      // The "ONLY 1 UNIT" RULE also applies on the receiving side (defensive, in case mode B is turned back on
      // or someone crafts a URL).
      const incomingViolation = findSingleUnitViolation(items);
      if (incomingViolation) {
        log('info', 'Product "' +
          (incomingViolation.item.product_title || incomingViolation.item.handle) +
          '" is limited to 1 unit, the incoming cart has ' + incomingViolation.units + ' -> not processed');
        return;
      }

      const resolved = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (!isPermalinkSafe(item)) {
          log('warn', 'Line cannot be carried over (receiving):', item.product_title);
          return;
        }
        // resolveLineItem fetches TARGET_BASE == this very origin -> SAME-ORIGIN, no CORS involved.
        const match = await resolveLineItem(item);
        if (!match) {
          log('warn', 'No match on the target store (receiving) -> staying on the page, no redirect:', item.product_title);
          return;
        }
        // The receiving side has no /cart form to read "pending" quantities from -> use the encoded quantity.
        const qty = (Number.isFinite(item.quantity) && item.quantity > 0) ? item.quantity : 1;
        resolved.push({ variantId: match.variant.id, quantity: qty });
      }

      const permalink = buildPermalink(resolved);
      if (permalink.length > MAX_URL_LENGTH) {
        log('warn', 'Permalink (receiving) too long -> staying on the page');
        return;
      }
      log('info', 'Received cart from the source store, redirecting to:', permalink);
      window.location.assign(permalink);
    } catch (e) {
      // Broken/undecodable parameter -> quietly stay on the page (we are on aldwynco, shopping works as usual).
      log('warn', 'Could not decode the incoming cart data -> ignored:', e);
    }
  };

  // ---- Find an unticked terms checkbox near the checkout button ------------
  // Because our listener runs in the CAPTURE phase (before terms handlers that run in the bubble phase),
  // we must check OURSELVES: if there is a terms checkbox that is NOT ticked -> do NOT intervene,
  // so the native terms handler runs and blocks checkout.
  const findUncheckedTerms = (button) => {
    let node = button.parentElement;
    while (node && node !== document.body) {
      const cb = node.querySelector('input[type="checkbox"][id^="CartTerms"]');
      if (cb) return cb.checked ? null : cb;
      node = node.parentElement;
    }
    return null;
  };

  // ---- TOP-PRIORITY CONDITION: detect & block the country by IP ----------
  // IP-geolocation endpoints (free, with CORS, no key needed). Tried in turn until one gives a result.
  const GEO_ENDPOINTS = [
    { url: 'https://get.geojs.io/v1/ip/country.json', field: 'country' },
    { url: 'https://ipwho.is/', field: 'country_code' },
    { url: 'https://ipapi.co/json/', field: 'country_code' },
  ];

  let visitorCountry = undefined; // undefined=lookup not finished; null=lookup failed; 'VN'/'US'...=known
  let visitorIp = undefined;      // undefined=lookup not finished; null=unavailable; '1.2.3.4'=known
  let networkPromise = null;      // single-flight: looked up once per session (country + IP in one lookup)

  // Ask the endpoints in turn until one gives the COUNTRY; take the IP from THAT same response too
  // (all three services return an "ip" field). Returns { country, ip }; either may be null if unreadable.
  const lookupVisitorFromNetwork = async () => {
    let ipOnly = null; // IP read from an endpoint even if that endpoint lacks the country code
    for (let i = 0; i < GEO_ENDPOINTS.length; i++) {
      const ep = GEO_ENDPOINTS[i];
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), GEO_LOOKUP_TIMEOUT_MS);
      try {
        const res = await fetch(ep.url, { signal: controller.signal, headers: { Accept: 'application/json' } });
        if (res.ok) {
          const data = await res.json();
          const ip = (data && typeof data.ip === 'string' && data.ip.trim()) ? data.ip.trim() : null;
          if (ip && !ipOnly) ipOnly = ip;
          const code = data && data[ep.field];
          if (code && typeof code === 'string') {
            return { country: code.trim().toUpperCase(), ip: ip || ipOnly };
          }
        }
      } catch (e) {
        /* endpoint error/timeout -> try the next endpoint */
      } finally {
        clearTimeout(timer);
      }
    }
    return { country: null, ip: ipOnly }; // no endpoint gave a country (but the ip may still be known)
  };

  // Look up IP + country ONCE per session (single-flight + sessionStorage cache). Sets visitorCountry/visitorIp.
  const resolveVisitorNetwork = () => {
    if (networkPromise) return networkPromise;
    networkPromise = (async () => {
      if (GEO_COUNTRY_OVERRIDE || GEO_IP_OVERRIDE) { // forced for testing (country and/or IP)
        visitorCountry = GEO_COUNTRY_OVERRIDE ? String(GEO_COUNTRY_OVERRIDE).trim().toUpperCase() : null;
        visitorIp = GEO_IP_OVERRIDE ? String(GEO_IP_OVERRIDE).trim() : null;
        return { country: visitorCountry, ip: visitorIp };
      }
      try {
        const cached = sessionStorage.getItem(GEO_SESSION_KEY);
        if (cached) { // only cached after a SUCCESSFUL lookup (with a country)
          // The new cache is JSON {c, ip}; an OLD cache may be just the country string -> still readable (ip=null).
          let c = cached, ip = null;
          try {
            const o = JSON.parse(cached);
            if (o && typeof o === 'object') { c = o.c || null; ip = o.ip || null; }
          } catch (e) { /* old string -> treat the whole string as the country code */ }
          visitorCountry = c; visitorIp = ip;
          return { country: c, ip: ip };
        }
      } catch (e) { /* sessionStorage error -> look up over the network */ }

      const r = await lookupVisitorFromNetwork();
      visitorCountry = r.country;
      visitorIp = r.ip;
      // Cache ONLY when there is a valid country code (the "lookup succeeded" mark). Failures (country=null) are
      // NOT cached -> the next page retries, so one temporary network error doesn't lock fail-closed for the whole session.
      if (r.country) {
        try { sessionStorage.setItem(GEO_SESSION_KEY, JSON.stringify({ c: r.country, ip: r.ip || null })); }
        catch (e) { /* ignore */ }
      }
      return r;
    })();
    return networkPromise;
  };

  // true if cross-store is ALLOWED by country. country: 'VN' | null | undefined | ''
  const isGeoAllowed = (country) => {
    if (!GEO_BLOCK_ENABLED) return true;
    if (!country) return PROCEED_IF_GEO_UNKNOWN;        // null/undefined/'' = could not be determined
    return NORM_BLOCKED_COUNTRIES.indexOf(country) === -1;
  };

  // true if the IP is NOT banned. ip: '1.2.3.4' | null | undefined. Empty list -> always allowed.
  // It is a BLOCKLIST: only blocks when the IP is KNOWN and on the list. IP unknown -> NOT blocked
  // here (the fail-closed country condition decides the "unknown" case).
  const isIpAllowed = (ip) => {
    if (!IP_BLOCK_ENABLED) return true;
    if (!ip) return true;                              // IP unknown -> can't say it is banned
    return NORM_BLOCKED_IPS.indexOf(normalizeIp(ip)) === -1;
  };

  // COMBINED network condition: both the specific-IP ban and the country block.
  // true = cross-store ALLOWED; false = at least one condition blocks.
  const isVisitorNetworkAllowed = (country, ip) => isIpAllowed(ip) && isGeoAllowed(country);

  // ---- TIME-ON-PAGE gate -------------------------------------------------
  // Uses performance.now() (a MONOTONIC clock counting from the start of page load) when available, so the
  // measurement is NOT skewed when the visitor changes the system time. Otherwise falls back to Date.now().
  const nowMs = () => (typeof performance !== 'undefined' && typeof performance.now === 'function')
    ? performance.now()
    : Date.now();

  // When the visitor STARTED on this page: set once when the script runs (every page load/navigation starts anew).
  const pageEnteredAt = nowMs();

  // true if the visitor has been on the current page LONG ENOUGH (>= MIN_TIME_ON_PAGE_MS). 0 -> always allowed.
  const hasStayedLongEnough = () => {
    if (!MIN_TIME_ON_PAGE_MS || MIN_TIME_ON_PAGE_MS <= 0) return true;
    return (nowMs() - pageEnteredAt) >= MIN_TIME_ON_PAGE_MS;
  };

  // Seconds spent on the page (only for easier-to-read logs).
  const secondsOnPage = () => Math.round((nowMs() - pageEnteredAt) / 100) / 10;

  // ---- aldwyn's daily ORDER caps -----------------------------------------
  // JS can't read orders -> ask the ENDPOINT you deployed.
  // SHORT cache (TTL) because the order count changes all day. null = numbers unavailable.
  let dailyStats = { value: null, at: 0 }; // value = {orders, siteOrders, productOrders} | null; at = nowMs() of the latest fetch
  let dailyStatsPromise = null;            // single-flight while in flight

  // Read a numeric JSON field -> number, or null if missing/not a number.
  const readNumber = (raw) => {
    if (raw === undefined || raw === null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  };

  // Hostname of the store running the script, normalized LIKE the worker (lowercase, no 'www.')
  // to look up the right key in by_referrer_paid / MAX_DAILY_ORDERS_PER_SITE.
  const currentSiteHost = String(location.hostname || '').toLowerCase().replace(/^www\./, '');

  // Time-step table { 'HH:MM': cap } -> the cap in effect NOW: the cap of the LATEST step already passed.
  // Before the first step -> use the first step's cap, NOT a wrap-around to the last step: the order count
  // resets at 00:00, and using the highest cap here would let too many orders through at the start of the day.
  const capFromSchedule = (schedule) => {
    const steps = Object.keys(schedule)
      .map((clock) => {
        const minute = parseClockValue(clock);
        if (minute === null) log('warn', 'Unreadable cap time step -> skipped:', clock);
        return minute === null ? null : { minute: minute, cap: schedule[clock] };
      })
      .filter(Boolean)
      .sort((a, b) => a.minute - b.minute);
    const now = currentMinuteOfDay();
    const passed = steps.filter((step) => step.minute <= now);
    const current = passed.length > 0 ? passed[passed.length - 1] : steps[0];
    return current ? current.cap : null;
  };

  // Read a cap from the config (plain number or time-step table) -> number.
  const readCap = (raw) => {
    const n = readNumber(raw && typeof raw === 'object' ? capFromSchedule(raw) : raw);
    return n === null ? 0 : n; // unreadable -> treated as 0 = no separate limit
  };

  // The current site's own threshold. Not listed in the table -> use the default value.
  const siteOrderCap = () => {
    const raw = Object.prototype.hasOwnProperty.call(MAX_DAILY_ORDERS_PER_SITE, currentSiteHost)
      ? MAX_DAILY_ORDERS_PER_SITE[currentSiteHost]
      : MAX_DAILY_ORDERS_PER_SITE_DEFAULT;
    return readCap(raw);
  };

  // PAID orders today that CAME FROM the current site, taken from the endpoint's by_referrer_paid.
  // Accepts both value forms: { orders, total } (new version) and a plain number (if only by_referrer exists).
  // A site with no orders yet -> NO key -> returns 0 (not null "unknown").
  const readSiteOrders = (data) => {
    const map = (data && data.by_referrer_paid) || (data && data.by_referrer);
    if (!map || typeof map !== 'object') return null; // endpoint does not return this field yet -> unknown
    if (!Object.prototype.hasOwnProperty.call(map, currentSiteHost)) return 0;
    const entry = map[currentSiteHost];
    return readNumber(entry && typeof entry === 'object' ? entry.orders : entry);
  };

  const fetchDailyStats = async () => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ORDER_TOTAL_TIMEOUT_MS);
    try {
      const res = await fetch(ORDER_TOTAL_ENDPOINT, {
        signal: controller.signal, headers: { Accept: 'application/json' },
      });
      if (!res.ok) return null;
      const data = await res.json();
      const orders = readNumber(data && data.orders);
      const siteOrders = readSiteOrders(data);
      if (orders === null && siteOrders === null) return null; // no number readable -> treated as a failure
      return { orders: orders, siteOrders: siteOrders, productOrders: data && data.by_product_paid };
    } catch (e) {
      return null; // error/timeout/CORS -> null (the caller applies fail-open/closed)
    } finally {
      clearTimeout(timer);
    }
  };

  // Get today's numbers: use the cache while fresh (< TTL); otherwise fetch anew (single-flight).
  const getDailyStats = () => {
    if (dailyStats.at && (nowMs() - dailyStats.at) < ORDER_TOTAL_TTL_MS) {
      return Promise.resolve(dailyStats.value);
    }
    if (dailyStatsPromise) return dailyStatsPromise;
    dailyStatsPromise = (async () => {
      const v = await fetchDailyStats();
      dailyStats = { value: v, at: nowMs() };
      dailyStatsPromise = null;
      return v;
    })();
    return dailyStatsPromise;
  };

  // true if cross-store is ALLOWED by the daily caps. stats: {orders, siteOrders} | null.
  // Checks TWO independent thresholds — exceeding EITHER one stops redirecting:
  //   (a) total orders          > MAX_DAILY_ORDERS   (set 0 to turn off just this threshold)
  //   (b) CURRENT SITE's orders >= that site's cap   (set 0 to turn off just this threshold)
  // NOTE the comparisons DIFFER between (a) and (b) — as requested:
  //   (a) uses '>'  : MAX_DAILY_ORDERS=5, 5 orders so far -> STILL runs, stops only at 6 orders.
  //   (b) uses '>=' : leanors cap=5, leanors has 5 orders -> STOPS right away (the 6th order uses native checkout).
  // The per-PRODUCT threshold (c) isn't here because it needs the cart read and matched first: see findCappedProduct.
  const isUnderDailyLimits = (stats) => {
    if (!DAILY_LIMIT_ENABLED || !ORDER_TOTAL_ENDPOINT) return true; // off / endpoint not configured
    if (!stats) return PROCEED_IF_LIMIT_UNKNOWN;                    // nothing could be fetched

    // (a) ORDER threshold
    if (Number.isFinite(MAX_DAILY_ORDERS) && MAX_DAILY_ORDERS > 0) {
      if (!Number.isFinite(stats.orders)) {
        if (!PROCEED_IF_LIMIT_UNKNOWN) return false; // order count missing + fail-closed -> block
      } else if (stats.orders > MAX_DAILY_ORDERS) {
        return false;
      }
    }

    // (b) The CURRENT SITE's own ORDER threshold
    const cap = siteOrderCap();
    if (cap > 0) {
      if (!Number.isFinite(stats.siteOrders)) {
        if (!PROCEED_IF_LIMIT_UNKNOWN) return false; // site order count missing + fail-closed -> block
      } else if (stats.siteOrders >= cap) {
        return false;
      }
    }

    return true;
  };

  // (c) A product's OWN cap: looked up by the aldwyn handle, then the current store's handle + title
  // -> any of these forms works in the config. Not in the table -> 0 = no separate limit.
  const productOrderCap = (item, product) => {
    const keys = [product.handle, item.handle, item.product_title];
    for (let i = 0; i < keys.length; i++) {
      const key = normalize(keys[i]);
      if (key && Object.prototype.hasOwnProperty.call(NORM_PRODUCT_CAPS, key)) {
        return readCap(NORM_PRODUCT_CAPS[key]);
      }
    }
    return 0;
  };

  // PAID orders today that CONTAIN this product, looked up by the aldwyn product id.
  // Product with no orders yet -> NO key -> returns 0. Endpoint doesn't return this table yet -> null (unknown).
  const readProductOrders = (stats, product) => {
    const map = stats && stats.productOrders;
    if (!map || typeof map !== 'object') return null;
    const key = String(product.id);
    if (!Object.prototype.hasOwnProperty.call(map, key)) return 0;
    const entry = map[key];
    return readNumber(entry && typeof entry === 'object' ? entry.orders : entry);
  };

  // Returns { product, orders, cap } of the FIRST product that hit its own cap, or null if the whole cart has room.
  // resolved: the matched lines, each with { item, product }. Uses '>=' like (b).
  const findCappedProduct = (resolved, stats) => {
    for (let i = 0; i < resolved.length; i++) {
      const product = resolved[i].product;
      const cap = productOrderCap(resolved[i].item, product);
      if (cap <= 0) continue;
      const orders = readProductOrders(stats, product);
      if (!Number.isFinite(orders)) {
        log('warn', 'No per-product order counts (by_product_paid) -> cannot apply the own cap of', product.handle);
        if (!PROCEED_IF_LIMIT_UNKNOWN) return { product: product, orders: orders, cap: cap }; // fail-closed -> block
      } else if (orders >= cap) {
        return { product: product, orders: orders, cap: cap };
      }
    }
    return null;
  };

  // Describe the current numbers, for easier-to-read logs.
  const describeDailyStats = (stats) => {
    if (!stats) return 'numbers unavailable';
    const cap = siteOrderCap();
    return 'orders=' + stats.orders + '/' + MAX_DAILY_ORDERS +
      ', ' + currentSiteHost + '=' + stats.siteOrders + '/' + (cap > 0 ? cap : '∞');
  };

  // Is there a FRESH copy of the numbers (within TTL) in the cache? (used by the interceptor's fast path)
  const hasFreshDailyStats = () => !!dailyStats.at && (nowMs() - dailyStats.at) < ORDER_TOTAL_TTL_MS;

  // ---- TIME WINDOW gate ---------------------------------------------------
  // Converts times to MINUTES SINCE 00:00 (0..1439) for simple comparisons.

  // Current time in ACTIVE_HOURS_TIMEZONE; empty -> the visitor's clock.
  const currentMinuteOfDay = () => {
    const d = new Date();
    if (ACTIVE_HOURS_TIMEZONE) {
      try {
        // formatToParts + timeZone: reads the TIME in the given zone, correct even when that place switches to daylight saving.
        const parts = new Intl.DateTimeFormat('en-US', {
          timeZone: ACTIVE_HOURS_TIMEZONE, hour12: false, hour: '2-digit', minute: '2-digit',
        }).formatToParts(d);
        let h = NaN, m = NaN;
        for (let i = 0; i < parts.length; i++) {
          if (parts[i].type === 'hour') h = parseInt(parts[i].value, 10);
          else if (parts[i].type === 'minute') m = parseInt(parts[i].value, 10);
        }
        // hour12:false returns '24' for midnight on some engines -> % 24 brings it back to 0.
        if (Number.isFinite(h) && Number.isFinite(m)) return (h % 24) * 60 + m;
      } catch (e) {
        log('warn', 'Invalid ACTIVE_HOURS_TIMEZONE (' + ACTIVE_HOURS_TIMEZONE +
          ') -> using the visitor clock:', e);
      }
    }
    return d.getHours() * 60 + d.getMinutes();
  };

  // Read a time value from the config -> minutes since 00:00. Accepts:
  //   integer 9 (9:00), decimal 9.5 (9:30), strings '9', '09:30', and 24 / '24:00' (end of day).
  // Unreadable -> null (that range is SKIPPED, checkout is never broken).
  const parseClockValue = (value) => {
    let h = NaN, m = 0;
    if (typeof value === 'number' && Number.isFinite(value)) {
      h = Math.floor(value);
      m = Math.round((value - h) * 60);
    } else {
      const matched = String(value === null || value === undefined ? '' : value)
        .trim().match(/^(\d{1,2})(?::(\d{1,2}))?$/);
      if (!matched) return null;
      h = parseInt(matched[1], 10);
      m = matched[2] ? parseInt(matched[2], 10) : 0;
    }
    if (!Number.isFinite(h) || !Number.isFinite(m) || h < 0 || m < 0 || m > 59) return null;
    const total = h * 60 + m;
    if (total > 1440) return null;          // past 24:00 -> invalid config
    return total;                            // 1440 = 24:00 (only meaningful as an END value)
  };

  // Normalize the config ONCE at page load (no re-parsing on every click).
  // Malformed ranges are dropped with a warning -> a bad config does NOT kill the checkout button.
  const NORM_ACTIVE_HOUR_RANGES = (Array.isArray(ACTIVE_HOUR_RANGES) ? ACTIVE_HOUR_RANGES : [])
    .map((range) => {
      if (!Array.isArray(range) || range.length !== 2) {
        log('warn', 'Malformed time range (expected [start, end]) -> skipped:', range);
        return null;
      }
      const start = parseClockValue(range[0]);
      const end = parseClockValue(range[1]);
      if (start === null || end === null) {
        log('warn', 'Time range with an unreadable value -> skipped:', range);
        return null;
      }
      return { start: start % 1440, end: end };  // keep 1440 as the end = end of day
    })
    .filter(Boolean);

  // true if the current MINUTE is within [start, end): the start is included, the end is NOT.
  const isInHourRange = (minuteOfDay, range) => {
    if (range.start === range.end) return false;                       // empty range (e.g. [9, 9])
    if (range.start < range.end) return minuteOfDay >= range.start && minuteOfDay < range.end;
    return minuteOfDay >= range.start || minuteOfDay < range.end;      // range ACROSS MIDNIGHT
  };

  // true if cross-store is ALLOWED by the time windows. Empty array -> always allowed.
  // Checked at CLICK TIME (not at page load) to follow the real time.
  const isWithinActiveHours = () => {
    if (NORM_ACTIVE_HOUR_RANGES.length === 0) return true;
    const now = currentMinuteOfDay();
    return NORM_ACTIVE_HOUR_RANGES.some((range) => isInHourRange(now, range));
  };

  // 'H:MM' for logs (easy to compare when reviewing the config).
  const clockLabel = (minuteOfDay) =>
    Math.floor(minuteOfDay / 60) + ':' + String(minuteOfDay % 60).padStart(2, '0');

  // Describe the active time windows, for the log.
  const describeActiveHours = () => NORM_ACTIVE_HOUR_RANGES
    .map((r) => clockLabel(r.start) + '-' + clockLabel(r.end % 1440)).join(', ');

  // ---- DEVICE gate: mobile only ------------------------------------------
  // Detection in order of DECREASING RELIABILITY:
  //  (1) UA Client Hints (navigator.userAgentData.mobile) — the browser's STANDARD boolean flag,
  //      not guessed from a string. Available on recent Chrome/Edge/Android.
  //  (2) The User-Agent string — the classic way, works in EVERY browser (including iOS Safari, Firefox).
  //  (3) Touch point count (maxTouchPoints) — used ONLY to catch iPads, see the explanation below.
  const detectIsMobileDevice = () => {
    if (DEVICE_TYPE_OVERRIDE) { // forced for testing
      return String(DEVICE_TYPE_OVERRIDE).trim().toLowerCase() === 'mobile';
    }

    const nav = (typeof navigator !== 'undefined' && navigator) ? navigator : {};
    const ua = String(nav.userAgent || '').toLowerCase();
    const touchPoints = Number(nav.maxTouchPoints) || 0;

    // (1) userAgentData.mobile === true -> DEFINITELY a phone, no more guessing.
    // Note: false does NOT prove a desktop (tablets report false too) -> keep checking.
    try {
      if (nav.userAgentData && nav.userAgentData.mobile === true) return true;
    } catch (e) { /* not supported by the browser -> use the User-Agent */ }

    // (2) PHONES by User-Agent.
    // Android: needs BOTH 'android' AND 'mobile' — Android without 'mobile' is a TABLET.
    const isPhone = /iphone|ipod|windows phone|iemobile|blackberry|bb10|opera mini|palm|webos/.test(ua) ||
      (/android/.test(ua) && /mobile/.test(ua));
    if (isPhone) return true;

    // (3) TABLETS — count as mobile only when TREAT_TABLET_AS_MOBILE is on.
    if (TREAT_TABLET_AS_MOBILE) {
      const isTablet = /ipad|tablet|kindle|silk|playbook/.test(ua) ||
        (/android/.test(ua) && !/mobile/.test(ua)) ||
        // iPadOS 13+ reports a User-Agent IDENTICAL to a Mac to "request the desktop site", so the UA string
        // can't tell them apart. Real Macs have no touchscreen -> maxTouchPoints > 1 means an iPad.
        // (Applies to 'macintosh' only: Windows laptops WITH touch must still count as desktop.)
        (/macintosh/.test(ua) && touchPoints > 1);
      if (isTablet) return true;
    }

    return false; // everything else: desktop / laptop
  };

  // Computed ONCE at page load — the device type doesn't change during the page's lifetime.
  const IS_MOBILE_DEVICE = detectIsMobileDevice();

  // true if cross-store is ALLOWED for this device. Condition off -> always allowed.
  const isDeviceAllowed = () => !MOBILE_ONLY_ENABLED || IS_MOBILE_DEVICE;

  // ---- Interceptor (document, CAPTURE phase) ------------------------------
  const onDocumentClickCapture = (event) => {
    let button = null;
    try {
      const el = event.target;
      if (!el || typeof el.closest !== 'function') return;

      button = el.closest(CHECKOUT_SELECTOR);
      if (!button) return;

      // A "bypass" submit we triggered ourselves -> let it through normally (no loop).
      if (button.dataset[BYPASS_ATTR] === 'true') return;

      // TOP-PRIORITY CONDITION (checked FIRST): specific-IP ban + block by the IP's country.
      // If the lookup is DONE (preloaded — visitorCountry is not undefined; visitorIp is there too since it
      // shares the same lookup) and not allowed -> do NOT intervene, let the native checkout run.
      // If NOT done yet (visitorCountry === undefined) -> fall through; handleCheckout will AWAIT
      // the result and go back to native checkout if blocked (so a banned/blocked IP is never redirected by mistake).
      if (NETWORK_GATE_ENABLED && visitorCountry !== undefined &&
        !isVisitorNetworkAllowed(visitorCountry, visitorIp)) {
        log('info', 'IP is banned or its country is blocked (ip=' + visitorIp +
          ', country=' + visitorCountry + ') -> using native checkout');
        return;
      }

      // DAILY CAPS (alongside): if FRESH numbers are ALREADY there and an order threshold is exceeded -> do
      // NOT intervene. Missing/expired -> fall through; handleCheckout will AWAIT fresh numbers and decide.
      if (DAILY_LIMIT_ENABLED && ORDER_TOTAL_ENDPOINT && hasFreshDailyStats() &&
        !isUnderDailyLimits(dailyStats.value)) {
        log('info', 'Aldwyn is over a daily threshold (' + describeDailyStats(dailyStats.value) +
          ') -> using native checkout');
        return;
      }

      // DEVICE GATE: desktop/laptop -> do NOT intervene, let the NATIVE checkout run as usual.
      if (!isDeviceAllowed()) {
        log('info', 'Device is not mobile -> using native checkout');
        return;
      }

      // "LONG ENOUGH ON PAGE" GATE: under MIN_TIME_ON_PAGE_MS -> do NOT intervene, let the native checkout run.
      if (!hasStayedLongEnough()) {
        log('info', 'Only ' + secondsOnPage() + 's on the page (needs ' + (MIN_TIME_ON_PAGE_MS / 1000) +
          's) -> using native checkout');
        return;
      }

      // TIME WINDOW GATE: outside the allowed ranges -> do NOT intervene at all, let the NATIVE checkout run.
      if (!isWithinActiveHours()) {
        log('info', 'Outside the allowed time windows (' + describeActiveHours() + '); now it is ' +
          clockLabel(currentMinuteOfDay()) + (ACTIVE_HOURS_TIMEZONE ? ' ' + ACTIVE_HOURS_TIMEZONE : '') +
          ' -> using native checkout');
        return;
      }

      // Unticked terms -> leave it to the native handler (no preventDefault, let the event keep bubbling).
      if (findUncheckedTerms(button)) {
        log('info', 'Terms checkbox not ticked -> leaving it to the native handler');
        return;
      }

      // Block the default submit (enough to stop it), then do the async work.
      event.preventDefault();
      event.stopPropagation();

      if (inFlight) { // double-click guard: swallow extra clicks while processing
        log('info', 'Already processing, ignoring repeated click');
        return;
      }
      inFlight = true;
      setBusy(button, true); // the button shows the theme's native loading animation while processing

      handleCheckout(button).catch((err) => {
        // Any error (CORS/network/unexpected) -> NEVER leave the button stuck; go back to native checkout.
        log('error', 'Error while handling cross-store checkout -> native checkout:', err);
        fallbackToNativeCheckout(button);
      });
    } catch (err) {
      // Absolute safety net: never leave the customer stuck on a dead button.
      log('error', 'Unexpected error in the interceptor -> native checkout:', err);
      try { if (button) fallbackToNativeCheckout(button); } catch (e) { /* ignore */ }
    }
  };

  // ---- Initialization -----------------------------------------------------
  // On the target store ITSELF -> do NOT attach the checkout interceptor.
  // BUT if there is cart data sent over by the source store (fallback mode), handle the "receiving" part.
  if (isTargetStore()) {
    let incoming = null;
    try { incoming = new URLSearchParams(location.search).get(INCOMING_PARAM); } catch (e) { /* ignore */ }
    if (ENABLE_DATA_REDIRECT_FALLBACK && incoming) {
      log('info', 'On the target store, received cart data from the source store -> matching same-origin');
      handleIncomingCart(incoming);
    } else {
      log('info', 'On the target store (' + location.hostname + ') -> skipping, using native checkout');
    }
    return;
  }

  // TOP-PRIORITY CONDITION: preload IP + country RIGHT at page load, so the result is ready when the visitor
  // clicks checkout (no waiting). Runs once per session (single-flight + sessionStorage cache).
  if (NETWORK_GATE_ENABLED) resolveVisitorNetwork();

  // Preload today's order totals so they are ready at click time (the short TTL cache refreshes itself when expired).
  if (DAILY_LIMIT_ENABLED && ORDER_TOTAL_ENDPOINT) getDailyStats();

  // PRELOAD the target store CATALOGUE RIGHT at page load: fills sessionStorage and "warms up" the cross-origin
  // connection (DNS/TLS/cookie) to aldwynco. So the FIRST CLICK already has matching data on hand, with no
  // download wait and no stumble on a first "cold" cross-origin fetch -> avoids wrongly dropping to the fallback
  // mode (redirect with ?aldwyn_xcart). THIS is exactly why a REFRESH used to be needed to get a direct permalink:
  // after a refresh the cache was warm. .catch() swallows background errors — a click still retries/or uses the fallback as before.
  getCatalogue().catch(function () { /* background preload error -> ignore, handled on click */ });

  // CAPTURE phase (true) to catch the button in BOTH the cart drawer (dynamically re-rendered) AND the /cart page,
  // while still yielding to the terms handler as above.
  document.addEventListener('click', onDocumentClickCapture, true);
  log('info', 'Cross-store checkout enabled. Target:', TARGET_BASE,
    '| device:', IS_MOBILE_DEVICE ? 'mobile' : 'desktop',
    MOBILE_ONLY_ENABLED ? '(mobile only)' : '(all devices)');
})();
